import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAppSelector, useAppDispatch } from '../../../../hooks/redux';
import { useDateFsnLocale } from '../../../../hooks/useDateFsnLocale';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { TextButton, TextButtonClassNames } from '../../../commons/buttons/TextButton';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import DateVO from '../../../../core/valueObjects/DateVO';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { AccountStatus, ModalVariants, ButtonClassNames } from '../../../../models/Common';
import { IMultiAccount } from '../../../../models/UserInfo';
import { ApiError } from '../../../../models/ApiError';
import { ReactComponent as OpenOptionsIcon } from '../../../../assets/images/multiAccount/openOptionsDots.svg';
import { getErrorMessage } from '../../../../utils';

interface ITableBody {
  item: IMultiAccount;
  handleToggleOptions: (id: number) => void;
  handleDeleteFromAdmin: (id: number) => void;
  handleViewServices: (item: IMultiAccount) => void;
  visibleAction: number;
}

export const TableBody: FC<ITableBody> = ({
  item,
  handleToggleOptions,
  handleViewServices,
  handleDeleteFromAdmin,
  visibleAction,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLUListElement | null>(null);
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const [resendInvitation, { isLoading: isResending }] = userInfoApi.useResendMemberInviteMutation();
  useClickOutside({
    ref: menuRef,
    onClose: () => handleToggleOptions(Infinity),
    enabled: visibleAction === item.id,
  });

  const handleOpenAccountSettings = (userId: number) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.SubAccountSettings,
        content: { userId },
      }),
    );
  };

  const handleSendAgain = async (userId: number) => {
    try {
      const res = await resendInvitation({ userId }).unwrap();
      const description =
        res.NotifyChannels.sms || res.NotifyChannels.email ? (
          <>
            {t('multiAccount.linkWasSent')}
            <br />
            <br />
            <span style={{ color: 'var(--color-error)' }}>{t('multiAccount.linkIsValidFor', { min: res.ttl })}</span>
            <br />
            <br />
            <span style={{ fontSize: '14px' }}>{t('multiAccount.confirmConnection')}</span>
          </>
        ) : (
          t('multiAccount.mustConfirm')
        );
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('success.balanceHasBeenReplenishedSuccessfully')}`,
            description: description as string,
          },
        }),
      );
    } catch (err: unknown) {
      const apiError = err as ApiError;
      if (apiError.status === 400) {
        dispatch(
          openModal({
            modalVariants: ModalVariants.ConfirmationsLimit,
            content: {
              notice: apiError?.data?.error?.message,
            },
          }),
        );
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  return (
    <div className={`multi-account__admin__table-row ${item.Role === 'admin' ? 'admin-row' : ''}`}>
      <div className="multi-account__admin__table-cell multi-account__admin__table-id">{item.UserID}</div>
      <div className="multi-account__admin__table-cell multi-account__admin__table-info">
        {item.Status === 'verified' ? (
          <TextButton
            label={`${item.User?.Address}`}
            handleClick={() => handleViewServices(item)}
            className={TextButtonClassNames.PrimaryActive}
          />
        ) : (
          '-'
        )}
      </div>
      <div className="multi-account__admin__table-cell multi-account__admin__table-info">
        {item.Status === 'verified' ? item.Alias || item.User?.Name : '-'}{' '}
        {item.Status === 'verified' && item.Role === 'admin' ? (
          <span className="multi-account__admin__table-admin-mark">({t('multiAccount.admin')})</span>
        ) : null}
      </div>
      <div className="multi-account__admin__table-cell multi-account__admin__table-info">
        {item.Status === 'verified' ? (
          <div
            className={`multi-account__admin__table-status-${
              item.Service?.Status === AccountStatus.Active ? 'active' : 'unpaid'
            }`}
          >
            {item.Service?.Status === AccountStatus.Active
              ? `${t('serviceManagement.nextPaymentDate')} ${format(
                  DateVO.create(item.Service?.ExpireDate).get(true),
                  selectedLanguage === 'en' ? 'MMMM d, yyyy' : 'd MMMM yyyy',
                  { locale: fnsLocale },
                )}`
              : t('multiAccount.noPayment')}
          </div>
        ) : (
          <div className="multi-account__admin__table-awaiting-confirm">
            {t('multiAccount.awaitingConfirm')}
            <br />
            <TextButton
              label={t('multiAccount.resendConfirm')}
              handleClick={() => handleSendAgain(item.UserID)}
              className={TextButtonClassNames.PrimaryActive}
              isDisabled={isResending}
            />
          </div>
        )}
      </div>
      <div className="multi-account__admin__table-cell multi-account__admin__table-options">
        <CustomMUIButton
          label={<OpenOptionsIcon />}
          handleClick={() => handleToggleOptions(item.id)}
          className={ButtonClassNames.IconGrayFulfilled}
          isDisabled={visibleAction === item.id}
        />
        {visibleAction === item.id && (
          <ul ref={menuRef} className="multi-account__admin__table-actions">
            {item.Role === 'admin' || item.Status === 'verified' ? (
              <li>
                <TextButton
                  label={t('multiAccount.viewServices')}
                  handleClick={() => handleViewServices(item)}
                  className="view-services"
                />
              </li>
            ) : null}
            {item.Role !== 'admin' && item.Status === 'verified' ? (
              <li>
                <TextButton
                  label={t('multiAccount.configure')}
                  handleClick={() => handleOpenAccountSettings(item.UserID)}
                  className="view-services"
                />
              </li>
            ) : null}
            {item.Role !== 'admin' && (
              <li>
                <TextButton
                  label={t('multiAccount.delete')}
                  handleClick={() => handleDeleteFromAdmin(item.UserID)}
                  className={TextButtonClassNames.Red}
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
